// src/pages/smscode.jsx — Reset Password (FIXED)
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Button, Input, OtpInput, Alert } from '../components/ui';
import { authApi } from '../services/api';

export default function ResetPasswordPage() {

  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState(0); // 0=email, 1=code, 2=newpass
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const startCountdown = () => {
    setCountdown(60);
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  // Step 0: Email yuborish
  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Email manzilini kiriting'); return; }
    setError(''); setIsLoading(true);
    try {
      await authApi.forgotPassword(email.trim());
      setStep(1);
      startCountdown();
      setSuccess('Tasdiqlash kodi emailingizga yuborildi');
    } catch (err) {
      setError(err.message || 'Xatolik yuz berdi');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 1: Code tasdiqlash (faqat UI, keyingi stepda server tekshiradi)
  const handleVerifyCode = (e) => {
    e.preventDefault();
    if (code.length < 6) { setError('6 xonali kodni kiriting'); return; }
    setError(''); setSuccess('');
    setStep(2);
  };

  // Step 2: Yangi parol
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { setError('Parol kamida 6 ta belgi bo\'lishi kerak'); return; }
    if (newPassword !== confirmPassword) { setError('Parollar mos kelmaydi'); return; }
    setError(''); setIsLoading(true);
    try {
      const data = await authApi.resetPassword({ email: email.trim(), code, newPassword });
      if (data.status === 'SUCCESS') {
        setSuccess('Parol muvaffaqiyatli yangilandi!');
        setTimeout(() => navigate('/login'), 1500);
      } else {
        setError(data.reason === 'INVALID_CODE' ? 'Noto\'g\'ri kod' : data.reason || 'Xatolik');
        if (data.reason === 'INVALID_CODE' || data.reason === 'OTP_EXPIRED') setStep(1);
      }
    } catch (err) {
      setError(err.message || 'Xatolik yuz berdi');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError(''); setIsLoading(true);
    try {
      await authApi.forgotPassword(email.trim());
      startCountdown();
      setSuccess('Yangi kod yuborildi');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const steps = [
    {
      icon: 'mail',
      iconBg: 'bg-[#1e9f6b]/10',
      iconColor: 'text-[#1e9f6b]',
      title: t('smsCode.title'),
      subtitle: t('smsCode.emailSubtitle'),
      content: (
        <form onSubmit={handleSendCode} className="flex flex-col gap-5">
          <Input
            label="Email"
            type="email"
            placeholder="email@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            icon="mail"
            autoFocus
            required
            className="h-14 font-black italic"
          />
          <Alert message={error} type="error" />
          <Button type="submit" size="xl" loading={isLoading} className="h-14 font-black uppercase tracking-widest">{t('smsCode.sendCode')}</Button>
        </form>
      ),
    },
    {
      icon: 'sms',
      iconBg: 'bg-blue-500/10',
      iconColor: 'text-blue-500',
      title: t('smsCode.verifyCode'),
      subtitle: email,
      content: (
        <form onSubmit={handleVerifyCode} className="flex flex-col gap-5">
          <OtpInput value={code} onChange={setCode} length={6} error={error ? ' ' : ''} />
          {error && <Alert message={error} type="error" />}
          {success && <div className="text-center text-sm text-[#1e9f6b] font-semibold">{success}</div>}
          <Button type="submit" size="xl" disabled={code.length < 6} className="h-14 font-black uppercase tracking-widest">
            Tasdiqlash
          </Button>
          <div className="text-center">
            {countdown > 0 ? (
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                {t('smsCode.resend')}: <span className="text-[#1e9f6b]">{countdown}s</span>
              </p>
            ) : (
              <button type="button" onClick={handleResend} disabled={isLoading}
                className="text-[#1e9f6b] text-[10px] font-black uppercase tracking-widest hover:underline disabled:opacity-50">{t('smsCode.resend')}</button>
            )}
          </div>
        </form>
      ),
    },
    {
      icon: 'lock_reset',
      iconBg: 'bg-amber-500/10',
      iconColor: 'text-amber-500',
      title: t('smsCode.newPasswordTitle'),
      subtitle: t('smsCode.newPasswordSubtitle'),
      content: (
        <form onSubmit={handleResetPassword} className="flex flex-col gap-5">
          <Input
            label={t("smsCode.newPassword")}
            type={showPwd ? 'text' : 'password'}
            placeholder="••••••••"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            icon="key"
            rightIcon={showPwd ? 'visibility_off' : 'visibility'}
            onRightIconClick={() => setShowPwd(!showPwd)}
            required
            className="h-14 font-black tracking-widest italic"
          />
          <Input
            label={t("smsCode.confirmPassword")}
            type={showConfirm ? 'text' : 'password'}
            placeholder="••••••••"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            icon="verified_user"
            rightIcon={showConfirm ? 'visibility_off' : 'visibility'}
            onRightIconClick={() => setShowConfirm(!showConfirm)}
            required
            className="h-14 font-black tracking-widest italic"
          />
          {error && <Alert message={error} type="error" />}
          {success && <div className="text-center text-sm text-[#1e9f6b] font-semibold">{success}</div>}
          <Button type="submit" size="xl" loading={isLoading} className="h-14 font-black uppercase tracking-widest shadow-xl shadow-[#1e9f6b]/30">{t('smsCode.save')}</Button>
        </form>
      ),
    },
  ];

  const current = steps[step];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/40 dark:from-[#080f0b] dark:via-[#0c1510] dark:to-[#080f0b] flex items-center justify-center px-4 py-8 overflow-hidden">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-32 -right-32 h-[500px] w-[500px] rounded-full bg-[#1e9f6b]/10 blur-[80px]" />
        <div className="absolute -bottom-32 -left-32 h-[400px] w-[400px] rounded-full bg-sky-400/8 blur-[80px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-10">
          <Link to="/login" className="flex items-center gap-4 group">
            <div className="h-14 w-14 bg-gradient-to-br from-[#1e9f6b] to-[#0d7a52] rounded-[22px] flex items-center justify-center shadow-2xl shadow-[#1e9f6b]/40 group-hover:scale-110 transition-transform duration-500">
              <span className="material-symbols-outlined text-white text-[28px] fill-1">local_gas_station</span>
            </div>
            <span className="text-3xl font-black text-slate-900 dark:text-white italic tracking-tighter">
              SMART<span className="text-[#1e9f6b]">GAZ</span>
            </span>
          </Link>
        </div>

        {/* Step dots */}
        <div className="flex gap-2.5 mb-8 px-4">
          {steps.map((_, idx) => (
            <div key={idx}
              className={`flex-1 h-2 rounded-full transition-all duration-700 ${
                idx <= step ? 'bg-gradient-to-r from-[#1e9f6b] to-[#0d7a52]' : 'bg-slate-200 dark:bg-slate-800'
              }`}
            />
          ))}
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-slate-900 rounded-[40px] border border-slate-100 dark:border-slate-800/50 shadow-2xl shadow-slate-900/10 p-10 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 blur-[40px] pointer-events-none" />
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col gap-7"
            >
              <div className="text-center">
                <div className={`h-20 w-20 ${current.iconBg} rounded-[28px] flex items-center justify-center mx-auto mb-6 shadow-xl`}>
                  <span className={`material-symbols-outlined ${current.iconColor} text-[36px]`}>{current.icon}</span>
                </div>
                <h2 className="text-2xl font-black text-slate-900 dark:text-white italic uppercase tracking-tight">
                  {current.title}
                </h2>
                <p className="text-slate-500 font-bold italic uppercase text-[10px] tracking-widest mt-2 px-4 opacity-70">
                  {current.subtitle}
                </p>
              </div>
              {current.content}
            </motion.div>
          </AnimatePresence>
        </div>

        <p className="text-center mt-8">
          <Link to="/login" className="text-[10px] font-black uppercase tracking-widest text-[#1e9f6b] hover:underline">
            ← {t('smsCode.backToLogin')}
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
